import React, { useEffect, useState } from 'react'
import { Box, Button, Typography, Alert, Divider, Grid } from '@mui/material'
import { useSearchParams } from 'react-router-dom-v5-compat'
import { FormProvider, useForm } from 'react-hook-form'
import SSOLogin from './SSOLogin'
import { get } from '../../utils/lodash'
import { useLazyGetAccountActiveQuery } from '../../api/account/getAccountStatus'
import HHFormTextField from '../../components/form-fields/v5/HHFormTextField'
import T from '../../T'

const VerifyAccount = ({ onBack }) => {
  const verifyAccountForm = useForm({ defaultValues: { accountNumber: '', invoiceNumber: '' } })
  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = verifyAccountForm

  const watchaccountNumber = watch('accountNumber')
  const watchinvoiceNumber = watch('invoiceNumber')
  const invoiceNumberHelperText = get(errors, 'invoiceNumber.message')
  const accountNumberHelperText = get(errors, 'accountNumber.message')
  const [getAccountActive, { data: currentAccount, isSuccess: isGetAccountActiveSuccess }] = useLazyGetAccountActiveQuery()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isActiveAccount, setIsActiveAccount] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [error, setError] = useState('')

  const onSubmitHandler = data => {
    if (!data.accountNumber || !data.invoiceNumber) {
      setError('Account Number and Invoice Number are required')
      return
    }

    try {
      getAccountActive({ ...data })
    } catch (err) {
      setError('Unable to verify account. Please try again.')
    }
  }

  useEffect(() => {
    if (isGetAccountActiveSuccess) {
      const businessName = get(currentAccount, 'businessName', '')
      const accountId = get(currentAccount, 'accountId', '')

      setSearchParams({ business_name: businessName, account_id: accountId })
      setIsActiveAccount(true)
    }
  }, [isGetAccountActiveSuccess])

  useEffect(() => {
    if (isActiveAccount) {
      setShowCreateForm(true)
    }
  }, [isActiveAccount])

  return (
    <>
      {!showCreateForm ? (
        <Box sx={{ p: 3, width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 1 }} align="center">
            Verify Account
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }} align="center">
            Enter your account details to verify your information
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormProvider {...verifyAccountForm}>
            <Grid size={12}>
              <HHFormTextField
                sx={{
                  my: 2,
                  '& fieldset': {
                    borderTop: 'none',
                    borderRight: 'none',
                    borderLeft: 'none',
                  },
                }}
                control={control}
                name="accountNumber"
                label="Account Number"
                placeholder="Enter your account number"
                rules={{ required: T.CANNOT_BE_EMPTY }}
                error={accountNumberHelperText}
                helperText={accountNumberHelperText}
                fullWidth
                deprecatedLabel={false}
              />
            </Grid>
            <Grid size={12}>
              <HHFormTextField
                sx={{
                  my: 2,
                  '& fieldset': {
                    borderTop: 'none',
                    borderRight: 'none',
                    borderLeft: 'none',
                  },
                }}
                control={control}
                name="invoiceNumber"
                rules={{ required: T.CANNOT_BE_EMPTY }}
                error={invoiceNumberHelperText}
                helperText={invoiceNumberHelperText}
                label="Invoice Number"
                placeholder="Enter your invoice number"
                fullWidth
                deprecatedLabel={false}
              />
            </Grid>

            <Box
              sx={{
                mt: 3,
                display: 'flex',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button variant="outlined" fullWidth onClick={() => onBack('create')} sx={{ order: { xs: 2, sm: 1 } }}>
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={handleSubmit(onSubmitHandler)}
                fullWidth
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Verify Account
              </Button>
            </Box>
          </FormProvider>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ p: 3, pb: 0 }}>
            <Typography variant="h6" sx={{ mb: 1 }} align="center">
              Step 2: Create your login
            </Typography>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }} align="center">
              Great, we found your account! Now, how would you like to Sign Up?
            </Typography>

            <Divider sx={{ mb: 3 }} />
          </Box>

          <SSOLogin key="sso-login" onBack={() => onBack('login')} accountVerified={isActiveAccount} />
        </Box>
      )}
    </>
  )
}

export default VerifyAccount
